import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import { mapValues, merge } from 'lodash'
import { useSelector } from 'react-redux'
import { RootState } from '../store'

const slots = {
  MapBar: false,
  MapRemixDrawer: false,
  MapDescriptionDrawer: false,
  MapFilterDrawer: false,
  MapLegend: false,
  MapLegendDrawer: false,
  MapSaveDrawer: false,
  MapSearch: false,
  MapSettingsDrawer: false,
  MapShareDrawer: false,
  MapTable: false,
  InviteGuestDrawer: false,
  MapCreateDrawer: false,
  MobileNav: false
}

const setAllSlots = (boolean) => mapValues(slots, () => boolean)

const setSlots = (paramSlot) => merge(setAllSlots(false), paramSlot)

const defaultOpen = setSlots({
  UserAvatar: true
})

type State = {
  open: typeof slots
  isAllToolsHidden: boolean
  isFullscreen: boolean
}

export const initialUiState: State = {
  open: defaultOpen,

  isAllToolsHidden: false,

  isFullscreen: false
}

export const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUiState,
  reducers: {
    openInviteGuestDrawer: (state) => {
      state.open.InviteGuestDrawer = true
    },

    closeInviteGuestDrawer: (state) => {
      state.open.InviteGuestDrawer = false
    },

    setFullscreen: (state, action: PayloadAction<State['isFullscreen']>) => {
      state.isFullscreen = action.payload
    },

    openMapFilterDrawer: (state) => {
      state.open = setSlots({
        MapFilterDrawer: true
      })
    },

    closeMapFilterDrawer: (state) => {
      state.open = setSlots({
        ...defaultOpen,
        MapFilterDrawer: false
      })
    },

    openLegend: (state) => {
      state.open = setSlots({
        MapLegend: true
      })
    },

    closeLegend: (state) => {
      state.open = defaultOpen
    },

    openMapSearch: (state) => {
      state.open = setSlots({
        MapSearch: true
      })
    },

    closeSearch: (state) => {
      state.open = defaultOpen
    },

    openTable: (state) => {
      state.open = setSlots({
        MapTable: true
      })
    },

    closeTable: (state) => {
      state.open = defaultOpen
    },

    openMapSettingsDrawer: (state) => {
      state.open = setSlots({
        MapSettingsDrawer: true
      })
    },

    closeMapSettingsDrawer: (state) => {
      state.open.MapSettingsDrawer = false
    },

    openMapDescriptionDrawer: (state) => {
      state.open = setSlots({
        MapDescriptionDrawer: true
      })
    },

    closeMapDescriptionDrawer: (state) => {
      state.open = defaultOpen
    },

    openMapRemixDrawer: (state) => {
      state.open = setSlots({
        MapBar: true,
        MapRemixDrawer: true
      })
    },

    closeMapRemixDrawer: (state) => {
      state.open = defaultOpen
    },

    openMapSaveDrawer: (state) => {
      state.open = setSlots({
        MapSaveDrawer: true
      })
    },

    closeMapSaveDrawer: (state) => {
      state.open = defaultOpen
    },

    hideAllTools: (state) => {
      state.isAllToolsHidden = true
    },

    showAllTools: (state) => {
      state.isAllToolsHidden = false
    },

    openMapShareDrawer: (state) => {
      state.open = setSlots({
        MapShareDrawer: true
      })
    },

    closeMapShareDrawer: (state) => {
      state.open = defaultOpen
    },

    openMapCreateDrawer: (state) => {
      state.open = setSlots({
        MapCreateDrawer: true
      })
    },

    closeMapCreateDrawer: (state) => {
      state.open = defaultOpen
    },

    openMobileNav: (state) => {
      state.open = setSlots({
        MobileNav: true
      })
    },

    closeMobileNav: (state) => {
      state.open = defaultOpen
    },

    openMapLegendDrawer: (state) => {
      state.open = setSlots({
        MapLegendDrawer: true
      })
    },

    closeMapLegendDrawer: (state) => {
      state.open.MapLegendDrawer = false
    },

    openMapLegend: (state) => {
      state.open.MapLegend = true
    },

    closeMapLegend: (state) => {
      state.open.MapLegend = false
    }
  }
})

export const {
  closeMapRemixDrawer,
  closeMapDescriptionDrawer,
  closeMapFilterDrawer,
  closeLegend,
  closeMapSaveDrawer,
  closeSearch,
  closeMapSettingsDrawer,
  closeTable,
  openMapRemixDrawer,
  openMapDescriptionDrawer,
  openMapFilterDrawer,
  openLegend,
  openMapSaveDrawer,
  openMapSearch,
  openMapSettingsDrawer,
  openTable,
  showAllTools,
  hideAllTools,
  openMapShareDrawer,
  closeMapShareDrawer,
  openMapCreateDrawer,
  closeMapCreateDrawer,
  openMobileNav,
  closeMobileNav,
  openMapLegendDrawer,
  closeMapLegendDrawer,
  openMapLegend,
  closeMapLegend,
  openInviteGuestDrawer,
  setFullscreen,
  closeInviteGuestDrawer
} = uiSlice.actions

const selectOpen = (state) => state.ui.open

const selectIsMapSearchOpen = createSelector(selectOpen, (open) => {
  return open.MapSearch
})

const selectIsMapTableOpen = createSelector(selectOpen, (open) => {
  return open.MapTable
})

const selectIsMapDescriptionDrawerOpen = createSelector(selectOpen, (open) => {
  return open.MapDescriptionDrawer
})

const selectIsMapRemixDrawerOpen = createSelector(selectOpen, (open) => {
  return open.MapRemixDrawer
})

const selectIsMapSettingsDrawerOpen = createSelector(selectOpen, (open) => {
  return open.MapSettingsDrawer
})

const selectIsMapSaveDrawerOpen = createSelector(selectOpen, (open) => {
  return open.MapSaveDrawer
})

const selectIsMapShareDrawerOpen = createSelector(selectOpen, (open) => {
  return open.MapShareDrawer
})

const selectIsMapFilterDrawerOpen = createSelector(selectOpen, (open) => {
  return open.MapFilterDrawer
})

const selectIsAllToolsHidden = (state) => state.ui.isAllToolsHidden

const selectIsMapCreateDrawerOpen = createSelector(selectOpen, (open) => {
  return open.MapCreateDrawer
})

const selectIsMapNavOpen = createSelector(selectOpen, (open) => {
  return open.MobileNav
})

const selectIsMapLegendDrawerOpen = createSelector(selectOpen, (open) => {
  return open.MapLegendDrawer
})

const selectIsMapLegendOpen = createSelector(selectOpen, (open) => {
  return open.MapLegend
})

const selectIsInviteGuestDrawerOpen = createSelector(selectOpen, (open) => {
  return open.InviteGuestDrawer
})

export const useOpen = () => useSelector(selectOpen)

export const useIsMapSearchOpen = () => useSelector(selectIsMapSearchOpen)

export const useIsMapTableOpen = () => useSelector(selectIsMapTableOpen)

export const useIsMapDescriptionDrawerOpen = () =>
  useSelector(selectIsMapDescriptionDrawerOpen)

export const useIsMapRemixDrawerOpen = () =>
  useSelector(selectIsMapRemixDrawerOpen)

export const useIsMapSettingsDrawerOpen = () =>
  useSelector(selectIsMapSettingsDrawerOpen)

export const useIsMapSaveDrawerOpen = () =>
  useSelector(selectIsMapSaveDrawerOpen)

export const useIsAllToolsHidden = () => useSelector(selectIsAllToolsHidden)

export const useIsMapShareDrawerOpen = () =>
  useSelector(selectIsMapShareDrawerOpen)

export const useIsMapFilterDrawerOpen = () =>
  useSelector(selectIsMapFilterDrawerOpen)

export const useIsMapLegendOpen = () => useSelector(selectIsMapLegendOpen)

export const useIsFullscreen = () =>
  useSelector((state: RootState) => state.ui.isFullscreen)

export const useIsMapCreateDrawerOpen = () =>
  useSelector(selectIsMapCreateDrawerOpen)

export const useIsMobileNavOpen = () => useSelector(selectIsMapNavOpen)

export const useIsMapLegendDrawerOpen = () =>
  useSelector(selectIsMapLegendDrawerOpen)

export const useIsInviteGuestDrawerOpen = () =>
  useSelector(selectIsInviteGuestDrawerOpen)

export default uiSlice.reducer
